import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generateUuid } from '../utils/uuid-helper';
import { CustomerInformation, Order, OrderStatus } from '../types/order';
import { BasketItem } from '../types/basket';
import { Supplier } from '../types/supplier';

interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  createOrder: (
    customerInfo: CustomerInformation, 
    items: BasketItem[], 
    subtotal: number, 
    vat: number, 
    total: number, 
    supplier?: Supplier,
    allSuppliers?: Supplier[]
  ) => Order;
  updateOrderStatus: (orderId: string, status: OrderStatus) => void;
  getOrderById: (orderId: string) => Order | undefined;
  getAllOrders: () => Order[];
  version: string; // Add version to help with migrations
}

export const generateOrderNumber = (): string => {
  // Format: ORD-YYYYMMDD-XXXX where XXXX is a random 4-digit number
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.floor(1000 + Math.random() * 9000); // Random 4-digit number
  
  return `ORD-${year}${month}${day}-${random}`;
};

export function buildTransientOrder(
  customerInfo: CustomerInformation,
  items: BasketItem[],
  subtotal: number,
  vat: number,
  total: number,
  supplier?: Supplier,
  allSuppliers?: Supplier[]
): Order {
  const nowIso = new Date().toISOString();
  return {
    id: generateUuid(),
    orderNumber: generateOrderNumber(),
    customerInfo,
    items: [...items],
    subtotal,
    vat,
    total,
    createdAt: nowIso,
    status: 'pending',
    supplier,
    allSuppliers,
  };
}

export const useOrderStore = create<OrderState>()(
  persist(
    (set, get) => ({
      orders: [],
      currentOrder: null,
      version: '1.0.1', // Version to help with migration
      
      createOrder: (customerInfo, items, subtotal, vat, total, supplier, allSuppliers) => {
        const newOrder: Order = buildTransientOrder(customerInfo, items, subtotal, vat, total, supplier, allSuppliers);
        
        set(state => ({
          orders: [...state.orders, newOrder],
          currentOrder: newOrder
        }));
        
        return newOrder;
      },
      
      updateOrderStatus: (orderId, status) => {
        set(state => ({
          orders: state.orders.map(order => 
            order.id === orderId ? { ...order, status } : order
          ),
          currentOrder: state.currentOrder?.id === orderId
            ? { ...state.currentOrder, status }
            : state.currentOrder
        }));
      },
      
      getOrderById: (orderId) => {
        return get().orders.find(order => order.id === orderId);
      },
      
      getAllOrders: () => {
        return get().orders;
      }
    }),
    {
      name: 'glass-calculator-orders',
      partialize: (state) => ({ 
        orders: state.orders,
        version: state.version 
      }),
      version: 1 // Storage version for migration
    }
  )
); 